import React, { useEffect, useRef } from 'react';
import type { ConversationItem } from '../types/conversationItem.types';
import CoachMessage from './messages/CoachMessage';

interface CoachFeedbackPanelProps {
  messages: ConversationItem[];
}

const CoachFeedbackPanel = ({ messages }: CoachFeedbackPanelProps) => {
  const panelRef = useRef<HTMLDivElement>(null);

  // Scroll to bottom when new messages are added
  useEffect(() => {
    // Small delay to ensure DOM has updated
    const scrollTimeout = setTimeout(() => {
      if (panelRef.current) {
        panelRef.current.scrollTo({
          top: panelRef.current.scrollHeight, // Scroll to bottom to show the newest message
          behavior: 'smooth',
        });
      }
    }, 100);

    return () => clearTimeout(scrollTimeout);
  }, [messages]); // Trigger scroll when messages array changes

  // No need to reverse messages anymore
  // const reversedMessages = [...messages].reverse();

  return (
    <div ref={panelRef} className="h-full overflow-y-auto">
      <div className="p-4">
        {messages.length > 0
          ? // Display messages in original order (oldest first)
            messages.map((message, index) => {
              // Calculate opacity and scale based on position (last is newest, first is oldest)
              const relativeIndex = messages.length - 1 - index; // 0 for newest, increases for older
              const opacity = Math.max(0.5, 1 - relativeIndex * 0.15); // Newest (relativeIndex 0) is 1.0 opacity
              const scale = Math.max(0.95, 1 - relativeIndex * 0.01); // Newest (relativeIndex 0) is 1.0 scale
              const isLastMessage = index === messages.length - 1; // Check if it's the last item in the original array (newest)

              return (
                // Add bottom padding and border, except for the last message (newest)
                <div
                  key={message.id}
                  className={`transform transition-all duration-500 ${
                    isLastMessage ? 'animate-slideUp' : '' // Animate the newest message sliding up
                  } ${!isLastMessage ? 'pb-4 mb-4 border-b border-secondary/20' : ''}`} // Add border to all but the newest
                  style={{
                    opacity,
                    // Add a subtle scale effect for older messages
                    transform: `scale(${scale})`,
                  }}
                >
                  <CoachMessage
                    id={message.id}
                    content={message.content}
                    time={message.time}
                    isInSidebar
                  />
                </div>
              );
            })
          : null}
      </div>
    </div>
  );
};

export default CoachFeedbackPanel;
